import { useEffect, useRef } from 'react'
import usePageVisibility from './usePageVisibility'
import MakeToast from 'ui/core/MakeToast'
import { fetchNextApi } from 'net/api'
import Analytics from 'lib/analytics'
import { useToast } from '@chakra-ui/react'
import moment from 'moment'
import { isPWAInstalled } from 'util/device'

const useAppVersionCheck = () => {
  const isPageVisible = usePageVisibility()
  const toast = useToast()
  const lastCalledAt = useRef<moment.Moment>()
  const isPWA = isPWAInstalled()

  useEffect(() => {
    const checkAppVersion = async () => {
      if (!isPageVisible) {
        // only care about the page coming back to visibility
        return
      }

      if (lastCalledAt.current?.isAfter(moment().subtract(30, 'minutes'))) {
        // we called this recently, chill out
        return
      }

      lastCalledAt.current = moment()

      try {
        const clientVersion = process.env.APP_VERSION
        if (!clientVersion) {
          throw new Error('Client APP_VERSION is not defined')
        }

        const res = await fetchNextApi<{ version: string }>('/api/appVersion')

        if (res.error) {
          throw new Error(res.error.message)
        }

        const serverVersion = res.response?.version

        if (serverVersion !== clientVersion) {
          const toastId = 'app-version-update'
          const action = isPWA ? 'Update' : 'Refresh'
          if (!toast.isActive(toastId)) {
            toast(
              MakeToast({
                id: toastId,
                title: 'New version available',
                description: 'Refresh to get the latest updates.',
                duration: null,
                status: 'info',
                onAction: () => {
                  Analytics.trackEvent('appVersion.updated')
                  window.location.reload()
                },
                actionLabel: action,
              })
            )
          }
        }
      } catch (e) {
        Analytics.trackEvent('appVersion.error', {
          message: e.message,
        })
      }
    }

    checkAppVersion()
  }, [isPageVisible, toast, isPWA])

  return null
}

export default useAppVersionCheck
